import type { FC, ReactNode } from 'react'
import type { ToolSummary } from '@/types'

import { cn } from '@/lib/utils'

interface ToolLogoProps {
    tool: Pick<ToolSummary, 'name' | 'logo' | 'logo_alt'>
    size?: 'sm' | 'md' | 'lg'
    className?: string
}

const sizes = {
    sm: 'size-8 rounded-lg text-sm',
    md: 'size-11 rounded-xl text-base',
    lg: 'size-16 rounded-2xl text-2xl'
}

// Stable hue per tool name, so a tool keeps the same monogram color everywhere.
const hueFor = (name: string): number => {
    let hash = 0

    for (const character of name) hash = (hash * 31 + character.charCodeAt(0)) % 360

    return hash
}

export const ToolLogo: FC<ToolLogoProps> = ({ tool, size = 'md', className }): ReactNode => {
    if (tool.logo) {
        return (
            <img
                src={tool.logo}
                alt={tool.logo_alt || `${tool.name} logo`}
                loading='lazy'
                className={cn('shrink-0 border border-border bg-secondary object-cover', sizes[size], className)}
            />
        )
    }

    const hue = hueFor(tool.name)
    const initial = tool.name.trim().charAt(0).toUpperCase() || '?'

    return (
        <span
            aria-hidden='true'
            className={cn('inline-flex shrink-0 items-center justify-center border border-white/5 font-heading font-bold', sizes[size], className)}
            style={{ backgroundColor: `oklch(0.3 0.07 ${hue})`, color: `oklch(0.92 0.09 ${hue})` }}
        >
            {initial}
        </span>
    )
}
